import React, { useState, useEffect, Component, ErrorInfo } from "react";
import BannerPromoItem from "../../components/bannerPromoSection/BannerPromoItem";
import "react-multi-carousel/lib/styles.css";
import { fetchBannerPromo } from "../../../domain/usecases/fetchBannerPromo";
import SpinnerLoading from "../../components/spinnerLoading/SpinnerLoading";

// Error Boundary Component
class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Error caught by boundary: ", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return <div>Something went wrong with the slider.</div>;
        }
        return this.props.children;
    }
}

// Main Component
const BannerPromoSection: React.FC = () => {
    const [banners, setBanners] = useState<string[]>([]);
    const [mobileBanners, setMobileBanners] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Fetch banners
    useEffect(() => {
        const fetchBanners = async () => {
            try {
                const bannerData = await fetchBannerPromo();
                
                // Extract and set both desktop and mobile banners
                const desktopPaths = bannerData.map((banner) => banner.path);
                const mobilePaths = bannerData.map((banner) => banner.path_apps);
                
                setBanners(desktopPaths);
                setMobileBanners(mobilePaths);
            } catch (err) {
                console.error("Failed to fetch banners:", err);
                setError("Failed to load banners. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchBanners();
    }, []);

    // Handle loading state
    if (loading) {
        return <SpinnerLoading />;
    }

    // Handle error state
    if (error) {
        return <div className="text-center text-red-500">{error}</div>;
    }

    // Get current banners based on device type
    const currentBanners = isMobile ? mobileBanners : banners;

    return (
        <div>
            <ErrorBoundary>
                <BannerPromoItem 
                    images={currentBanners}
                    autoSlideInterval={5000}
                />
            </ErrorBoundary>
        </div>
    );
};

export default BannerPromoSection;